import React from 'react';
import { Box, Skeleton } from '@mui/material';
import {
  banner, AVATAR_SIZE, BANNER_PULL_UP, BANNER_MASK_SX, NEUTRAL_BANNER_BASE,
  BANNER_ROW_PY, BANNER_ROW_PX, BANNER_BLEED_MX,
} from './headerLayout';

/** メディア切替バーの場所取り。寸法は MediaSegmentedControl と同じ箱で作る */
const MediaBarPlaceholder: React.FC = () => (
  <Box sx={{ mt: -1, mb: 1.5 }}>
    <Box
      sx={{
        display: 'inline-flex',
        borderRadius: '50px',
        p: '3px',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        backgroundColor: 'rgba(255, 255, 255, 0.03)',
      }}
    >
      {[46, 46, 34].map((w, i) => (
        <Box
          key={i}
          sx={{
            px: { xs: 1.4, sm: 2 },
            py: 0.4,
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
          }}
        >
          <Skeleton width={w} />
        </Box>
      ))}
    </Box>
  </Box>
);

interface ProfileHeaderSkeletonProps {
  /**
   * メディア切替バーの場所を取るか。
   *
   * 自分のページでは「自分で非表示にしていなければ必ず出る」ので、
   * データを待たずに端末設定だけで分かる。分かるなら取っておく
   * (取らないとバーが後から現れて、その下が 36px ぶん飛ぶ)。
   * 他人のページは相手のデータ次第なので予測できない＝取らない。
   */
  reserveMediaBar?: boolean;
}

/**
 * プロフィールヘッダーの読み込み中表示。
 *
 * **本体と同じ寸法・同じ位置で出すこと。** 寸法は headerLayout から取っていて、
 * 数値を直書きしていない。以前は高さに `banner.height.md`(420px) を決め打ちして
 * いたため、実寸が 216px の SP では切り替わりで 200px 以上ジャンプしていた。
 * さらにアバター行が無かったので、その下のタブやコンテンツも一緒に飛んでいた。
 *
 * 下地は誰のものでもない無彩色(NEUTRAL_BANNER_BASE)。ユーザー名シードのグラデを
 * ここで描くと、データ確定時に色相が飛ぶ。
 *
 * 紹介文の面は「有るか無いか」が読み込み前には分からないので**場所を取らない**。
 * 取ると紹介文の無い人で必ず余白が消えるぶんズレる。
 */
const ProfileHeaderSkeleton: React.FC<ProfileHeaderSkeletonProps> = ({
  reserveMediaBar = false,
}) => (
  <>
    <Box sx={{ position: 'relative', mt: BANNER_PULL_UP, mx: BANNER_BLEED_MX }}>
      <Box
        sx={{
          position: 'relative',
          height: banner.height,
          overflow: 'hidden',
          // 本体と同じマスクで下端を消す。ここを省くと切り替わりで境目が出入りする
          maskImage: BANNER_MASK_SX,
          WebkitMaskImage: BANNER_MASK_SX,
          background: NEUTRAL_BANNER_BASE,
        }}
      />
      {/* バナー下端の1行。位置(bottom / px / py / gap)は本体と一致させること */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: banner.extraDepth,
          px: BANNER_ROW_PX,
          py: BANNER_ROW_PY,
          display: 'flex',
          alignItems: 'flex-end',
          gap: { xs: 1.5, md: 2 },
        }}
      >
        <Skeleton
          variant="circular"
          sx={{ width: AVATAR_SIZE, height: AVATAR_SIZE, flexShrink: 0 }}
        />
        {/* 名前 + @ID の2行。本体は alignSelf:'center' なので合わせる */}
        <Box sx={{ flex: 1, minWidth: 0, alignSelf: 'center' }}>
          <Skeleton width="42%" sx={{ maxWidth: 220, fontSize: { xs: '0.8rem', md: '1.1rem' } }} />
          <Skeleton width="28%" sx={{ maxWidth: 140, fontSize: '0.875rem' }} />
        </Box>
      </Box>
    </Box>
    {reserveMediaBar && <MediaBarPlaceholder />}
    {/* メインタブの帯。本体の Tabs と同じ高さ・同じ下線を置いて、
        タブ列が後から現れることで下のコンテンツが飛ばないようにする */}
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        mb: 2,
        minHeight: { xs: 36, sm: 40 },
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: { xs: 1, sm: 1.5 },
      }}
    >
      {[64, 56, 48, 52].map((w, i) => (
        <Skeleton key={i} width={w} sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }} />
      ))}
    </Box>
  </>
);

export default ProfileHeaderSkeleton;
